import React, { useMemo, useState, useRef, useEffect } from 'react'
import { avatars, Avatar } from '@/data/avatars'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XIcon,
} from 'lucide-react'
import { ScriptWriter } from './ScriptWriter'
import { LoadingState } from './LoadingState'
import { VideoPlayer } from './VideoPlayer'
import { CustomizeModal } from './CustomizeModal'
import { SwipeableAvatar } from './SwipeableAvatar'

import { useMutation } from '@tanstack/react-query'
import phoenixService from '@/services/phoenixSocket'

import avatarCover from '@/assets/avatar_card.png'

import { AvatarLipSyncRequest, useAvatarLipSyncRequest } from '@/services/api_client'

type Step = 'select' | 'script' | 'loading' | 'preview'

type FilterType = {
  gender: string
  ageRange: string
}

interface AvatarSelectorProps {
  targetRef: React.RefObject<HTMLElement>
}

const apiUrl = import.meta.env.VITE_API_BASE_URL

export const AvatarSelector: React.FC<AvatarSelectorProps> = ({ targetRef, promo, user, userCredits }) => {
  const [requestId, setRequestId] = useState<string | null>(null)
  const [script, setScript] = useState('')
  const [duration, setDuration] = useState<number | null>(null)
  const [selectedAvatarId, setSelectedAvatarId] = useState<string | null>(null)
  const [currentImageIndices, setCurrentImageIndices] = useState<
    Record<string, number>
  >({})
  const [playingAudioId, setPlayingAudioId] = useState<string | null>(null)
  const [playingVideoId, setPlayingVideoId] = useState<string | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [currentStep, setCurrentStep] = useState<Step>('select')
  const [generationProgress, setGenerationProgress] = useState(0)
  const [videoUrl, setVideoUrl] = useState('')
  const [audioUrl, setAudioUrl] = useState('')
  const [isCustomizeModalOpen, setIsCustomizeModalOpen] = useState(false)
  const [aspectRatio, setAspectRatio] = useState('')
 
  const handleCustomAvatarLink = () => {
    if (user) {
      window.open(`${apiUrl}/avatars/new`, '_blank')
    } else {
      setIsCustomizeModalOpen(true)
    }
  }

  const resetAvatarBuilderState = () => {
    setCurrentStep('select')
    setSelectedAvatarId(null)
    setScript('')
    setDuration(null)
    setVideoUrl('')
    setAudioUrl('')
    setAspectRatio('')
    setSelectedAvatarId(null)

    localStorage.removeItem('avatarLipSyncRequest')
    localStorage.removeItem('selectedAvatarId')
    localStorage.removeItem('currentStep')
    localStorage.removeItem('requestId')
  }

  const { 
      createAvatarLipSyncRequest, 
      loading: isLoadingAvatar, 
      response: avatarResponse,
      completed: avatarCompleted,
      error: avatarError
   } = useAvatarLipSyncRequest();

  const selectedAvatar = useMemo(
    () => avatars.find((avatar) => avatar.id === selectedAvatarId),
    [selectedAvatarId],
  )

  const [filters, setFilters] = useState<FilterType>({
    gender: '',
    ageRange: ''
  })

  const filterOptions = {
    gender: ['male', 'female'],
    ageRange: ['18-25', '26-35', '36-45', '46+']
  }

  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(8)
  const pageSizeOptions = [8, 12, 16, 24]

  useEffect(() => {

      const selectedAvatarId = localStorage.getItem('selectedAvatarId')
      const currentStep = localStorage.getItem('currentStep')

      try {
        const avatarLipSyncRequest = JSON.parse(localStorage.getItem('avatarLipSyncRequest'))
      } catch (e) {
        const avatarLipSyncRequest = null
      }

    const requestId = localStorage.getItem('requestId')

    if (avatarResponse && avatarCompleted) {
        setVideoUrl(avatarResponse.data.video_url)
        setRequestId(avatarResponse.data.id)
        setCurrentStep('preview')
        localStorage.setItem('currentStep', 'preview')
    }

  }, [avatarResponse, avatarCompleted, avatarError])

  const filteredAvatars = useMemo(() => {
    return avatars.filter((avatar) => {
      if (!promo && avatar.promo == true) return false;

      // Skip filtering for the custom avatar
      if (avatar.id === 'custom') return true;
      
      // Filter by gender if selected
      if (filters.gender && avatar.gender) {
        if (avatar.gender.toLowerCase() !== filters.gender.toLowerCase()) {
          return false;
        }
      }
      
      // Filter by age range if selected
      if (filters.ageRange && avatar.age) {
        const [minAge, maxAge] = filters.ageRange.split('-').map(Number);
        
        if (maxAge) {
          // Normal age range like "18-25"
          if (avatar.age < minAge || avatar.age > maxAge) {
            return false;
          }
        } else {
          // Handle "46+" case
          const minAgeValue = parseInt(filters.ageRange);
          if (avatar.age < minAgeValue) {
            return false;
          }
        }
      }
      
      return true;
    });
  }, [filters])

  const totalPages = Math.ceil(filteredAvatars.length / pageSize)

  const paginatedAvatars = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    const end = start + pageSize
    return filteredAvatars.slice(start, end)
  }, [filteredAvatars, currentPage, pageSize])

  const promoAvatars = useMemo(() => {
    return avatars.filter((avatar) => avatar.promo)
  }, [filteredAvatars])

  const renderFilters = () => (
    <div className="px-4 py-3 border-b border-gray-100">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-2">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm font-medium text-gray-700">Gender:</span>
            {filterOptions.gender.map((gender) => (
              <button
                key={gender}
                onClick={() => {
                  setFilters((prev) => ({
                    ...prev,
                    gender: prev.gender === gender ? '' : gender,
                  }));
                  setCurrentPage(1);
                }}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${filters.gender === gender ? 'bg-purple-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {gender.charAt(0).toUpperCase() + gender.slice(1)}
                {filters.gender === gender && (
                  <XIcon className="inline-block ml-1" size={14} />
                )}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm font-medium text-gray-700">Age:</span>
            {filterOptions.ageRange.map((range) => (
              <button
                key={range}
                onClick={() => {
                  setFilters((prev) => ({
                    ...prev,
                    ageRange: prev.ageRange === range ? '' : range,
                  }));
                  setCurrentPage(1);
                }}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${filters.ageRange === range ? 'bg-purple-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {range}
                {filters.ageRange === range && (
                  <XIcon className="inline-block ml-1" size={14} />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )

  interface PaginationProps {
    style: 'normal' | 'minimal';
  }

  const renderPagination = ({ style }: PaginationProps) => (
    <div className={`px-4 py-3 ${style === 'normal' ? 'border-t' : ''} border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4`}>
      { style === "normal" && <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600">Show:</span>
        <select
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value))
            setCurrentPage(1)
            style === "normal" && scrollToTop()
          }}
          className="px-2 py-1 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size} avatars
            </option>
          ))}
        </select>
      </div>}
      <div className="flex items-center gap-2">
        <button
          onClick={() => { 
            setCurrentPage((p) => Math.max(1, p - 1))
            style === "normal" && scrollToTop()
          }}
          disabled={currentPage === 1}
          className={`p-2 rounded-lg border ${currentPage === 1 ? 'border-gray-100 text-gray-300 cursor-not-allowed' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
          aria-label="Previous page"
        >
          <ChevronLeftIcon size={16} />
        </button>
        <div className="flex items-center gap-1">
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              onClick={() => {
                setCurrentPage(i + 1)
                style === "normal" && scrollToTop()
              }}
              className={`min-w-[32px] h-8 rounded-lg text-sm font-medium ${currentPage === i + 1 ? 'bg-purple-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              {i + 1}
            </button>
          ))}
        </div>
        <button
          onClick={() => { 
            setCurrentPage((p) => Math.min(totalPages, p + 1))
            scrollToTop()
          }}
          disabled={currentPage === totalPages}
          className={`p-2 rounded-lg border ${currentPage === totalPages ? 'border-gray-100 text-gray-300 cursor-not-allowed' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
          aria-label="Next page"
        >
          <ChevronRightIcon size={16} />
        </button>
      </div>
    </div>
  )

  const generateVideo = async () => {
    setCurrentStep('loading')
    scrollToTop();

    const selectedImageIndex = selectedAvatarId !== null ? currentImageIndices[selectedAvatarId] || 0 : 0;
    const videoUrl = selectedAvatar.videos?.[selectedImageIndex] || '';
    const imageUrl = selectedAvatar.images?.[selectedImageIndex] || '';
    const aspectRatio = selectedAvatar.aspectRatios?.[selectedImageIndex] || '9:16';

    setAspectRatio(aspectRatio)

    const request: AvatarLipSyncRequest = {
        voice_id: selectedAvatar?.voiceId || '',
        audio_url: audioUrl,
        video_url: videoUrl,
        image_url: imageUrl,
        duration: duration,
        text: script,
        user_id: user?.user_id,
        aspect_ratio: aspectRatio,
    };

    localStorage.setItem('avatarLipSyncRequest', JSON.stringify(request))
    localStorage.setItem('currentStep', 'loading')

    await createAvatarLipSyncRequest(request)
  }

  const scrollToTop = () => {
    setTimeout(() => {
       //Option 2: Or scroll to the target element if you prefer
       if (targetRef.current) {
         targetRef.current.scrollIntoView({ behavior: 'smooth' });
       }
    }, 100);
  }

  const handleConfirm = () => {
    if (selectedAvatar) {
      scrollToTop()
      setCurrentStep('script')
      localStorage.setItem('selectedAvatarId', selectedAvatar.id)
      localStorage.setItem('currentStep', 'script')
    }
  }

  const handleImageChange = (
    avatarId: string,
    direction: 'next' | 'prev',
    e: React.MouseEvent | React.TouchEvent,
  ) => {
    e.stopPropagation()
    setCurrentImageIndices((prev) => {
      const avatar = avatars.find((a) => a.id === avatarId)
      if (!avatar) return prev
      const currentIndex = prev[avatarId] || 0
      let nextIndex = currentIndex
      if (direction === 'next') {
        nextIndex =
          currentIndex >= avatar.images.length - 1 ? 0 : currentIndex + 1
      } else {
        nextIndex =
          currentIndex === 0 ? avatar.images.length - 1 : currentIndex - 1
      }
      return {
        ...prev,
        [avatarId]: nextIndex,
      }
    })
  }
  const togglePlayAudio = (avatarId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    const avatar = avatars.find((a) => a.id === avatarId)
    
    if (!avatar) return
    
    // Check if this avatar has a video sample
    if (avatar.videoSample) {
      // Handle video - toggle playing state
      if (playingVideoId === avatarId) {
        setPlayingVideoId(null)
      } else {
        // Stop any playing audio
        if (playingAudioId) {
          const audioElement = document.querySelector('audio') as HTMLAudioElement
          audioElement?.pause()
          setPlayingAudioId(null)
        }
        
        // Stop any playing video
        if (playingVideoId) {
          setPlayingVideoId(null)
        }
        
        // Set new video as playing
        setPlayingVideoId(avatarId)
      }
    } else {
      // Handle audio (original behavior)
      const audioElement = document.querySelector('audio') as HTMLAudioElement
      
      if (playingAudioId === avatarId) {
        audioElement?.pause()
        setPlayingAudioId(null)
      } else {
        // Stop any playing audio
        if (playingAudioId) {
          audioElement?.pause()
        }
        
        // Stop any playing video
        if (playingVideoId) {
          setPlayingVideoId(null)
        }
        
        if (audioElement) {
          audioElement.src = avatar.voiceSample
          audioElement.play()
        }
        setPlayingAudioId(avatarId)
      }
    }
  }
  const handleMediaEnded = () => {
    setPlayingAudioId(null)
    setPlayingVideoId(null)
  }
  const handleAvatarClick = (avatarId: string) => {
    setSelectedAvatarId((prev) => (prev === avatarId ? null : avatarId))
  }
  const renderAvatarCard = (avatar: Avatar) => {
    if (avatar.id === 'custom') {
      return (
        <div
          key={avatar.id}
          className="relative rounded-lg overflow-hidden transition-all duration-200 cursor-pointer aspect-[3/4] sm:aspect-[9/16] group hover:shadow-lg"
          onClick={() => handleCustomAvatarLink()}
        >
          <div className="absolute inset-0">
            <img
              src={avatarCover}
              alt="Create custom avatar"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 bg-gradient-to-b from-black/40 via-black/60 to-black/80 backdrop-blur-[2px]">
            <div className="bg-white/10 p-6 rounded-xl backdrop-blur-sm w-full border border-white/10">
              <h3 className="text-xl font-semibold text-white mb-2">
                Create Custom Avatar
              </h3>
              <p className="text-white/80 text-sm">
                Design your perfect digital twin with advanced customization
              </p>
              <button className="mt-4 px-4 py-2 bg-white text-gray-900 rounded-lg font-medium hover:bg-gray-100 transition-colors cursor-pointer">
                Create Now
              </button>
            </div>
          </div>
        </div>
      )
    }
    return (
      <SwipeableAvatar
        key={avatar.id}
        avatar={avatar}
        isSelected={avatar.id === selectedAvatarId}
        currentImageIndex={currentImageIndices[avatar.id] || 0}
        playingAudioId={playingAudioId}
        playingVideoId={playingVideoId}
        onImageChange={(direction, e) =>
          handleImageChange(avatar.id, direction, e)
        }
        onAvatarClick={() => handleAvatarClick(avatar.id)}
        onConfirm={(e) => {
          e.stopPropagation()
          handleConfirm()
        }}
        onPlayAudio={(e) => togglePlayAudio(avatar.id, e)}
        onMediaEnd={() => handleMediaEnded()}
      />
    )
  }
  if (currentStep === 'script' && selectedAvatar) {
    return (
      <ScriptWriter
        script={script}
        setScript={setScript}
        setDuration={setDuration}
        avatar={selectedAvatar}
        selectedImageIndex={selectedAvatarId !== null ? currentImageIndices[selectedAvatarId] || 0 : 0}
        setAudioUrl={(url: string | null) => setAudioUrl(url || '')}
        audioUrl={audioUrl}
        duration={duration || 0}
        onBack={() => setCurrentStep('select')}
        onSubmit={generateVideo}
        user={user}
        userCredits={userCredits}
      />
    )
  }
  if (currentStep === 'loading') {
    return <LoadingState timeInSeconds={34} />
  }
  if (currentStep === 'preview') {
    return (
      <VideoPlayer
        user={user}
        userCredits={userCredits}
        videoUrl={videoUrl}
        requestId={requestId}
        aspectRatio={aspectRatio}
        selectedAvatar={selectedAvatar && {
          image: selectedAvatar.images[selectedAvatarId !== null ? currentImageIndices[selectedAvatarId] || 0 : 0],
          name: selectedAvatar.name
        }}
        onRestart={() => {
          setCurrentStep('select')
          setSelectedAvatarId(null)
        }}
        resetAvatarBuilderState={resetAvatarBuilderState}
      />
    )
  }
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-100 flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-800">
          Select an avatar to generate your video
        </h3>
        <div>{!promo && renderPagination({ style: 'minimal' })}</div>
      </div>
      {!promo && renderFilters()}
      <div className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {promo ? promoAvatars.map(renderAvatarCard) : paginatedAvatars.map(renderAvatarCard)}
        </div>
      </div>
      {!promo && renderPagination({ style: 'normal' })}
      <CustomizeModal
        isOpen={isCustomizeModalOpen}
        onClose={() => setIsCustomizeModalOpen(false)}
      />
    </div>
  )
}

